/**
 * Creating a customer.
 *
 * The first administrator's email is optional but strongly wanted: without it
 * the organization exists and nobody can get into it, and somebody has to
 * notice and come back. The form says so rather than letting it pass silently.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import own from './Console.module.css';
import page from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import { ADDON_LABEL } from './AddonsSection';
import type { AddonKey } from '../org/useAddons';
import field from '../admin/Admin.module.css';
import { EMAIL_MAX, ID_MAX, ORG_NAME_MAX, URL_MAX } from '../../types/fieldLimits';

/** Mirrors generateSlug on the backend, so the preview is not a lie. */
interface CreateResult {
  orgId: string;
  emailSent?: boolean;
  emailReason?: string | null;
  message?: string;
}

export function previewSlug(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function NewOrganizationPage() {
  const navigate = useNavigate();

  // A demo request can hand its details across in the query string.
  const [params] = useSearchParams();
  const [name, setName] = useState(params.get('name') ?? '');
  const [slug, setSlug] = useState('');
  const [primaryDomain, setPrimaryDomain] = useState('');
  const [systemAdminEmail, setSystemAdminEmail] = useState(params.get('sysadmin') ?? '');
  const [addons, setAddons] = useState<AddonKey[]>([]);
  const [touched, setTouched] = useState(false);

  const create = useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      call<Record<string, unknown>, CreateResult>('sysAdminCreateOrganization', input),
    // The organization page shows the invitation with Resend; go there
    // unless the email failed, in which case say why first.
    onSuccess: (result) => { if (result.emailSent !== false) navigate(`/console/orgs/${result.orgId}`); },
  });

  const effectiveSlug = previewSlug(slug || name);
  const nameTooShort = touched && name.trim().length < 2;
  const slugTooShort = touched && effectiveSlug.length < 3;
  const emailInvalid = touched && !systemAdminEmail.trim().includes('@');

  function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (name.trim().length < 2 || effectiveSlug.length < 3) return;
    if (!systemAdminEmail.trim().includes('@')) return;

    create.mutate({
      name: name.trim(),
      slug: slug.trim() || undefined,
      primaryDomain: primaryDomain.trim() || undefined,
      systemAdminEmail: systemAdminEmail.trim(),
      addons,
    });
  }

  return (
    <>
      <Link to="/console/orgs" className={own.link} style={{ fontSize: 13 }}>
        &larr; All organizations
      </Link>
      <h1 className={own.title} style={{ marginTop: 'var(--sp-4)' }}>
        New organization
      </h1>

      {create.error && (
        <div className={own.error} role="alert">
          {isCallFailure(create.error) ? create.error.message : 'Could not create the organization.'}
        </div>
      )}

      <form onSubmit={submit} noValidate style={{ maxWidth: 560 }}>
        <div className={form.field} style={{ marginBottom: 'var(--sp-5)' }}>
          <label className={form.label} htmlFor="org-name">Name</label>
          <input
            id="org-name"
            maxLength={ORG_NAME_MAX}
            className={form.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={nameTooShort}
            aria-describedby={nameTooShort ? 'org-name-error' : undefined}
            disabled={create.isPending}
          />
          {nameTooShort && (
            <p className={form.error} id="org-name-error">
              Give the organization a name of at least two characters.
            </p>
          )}
        </div>

        <div className={form.field} style={{ marginBottom: 'var(--sp-5)' }}>
          <label className={form.label} htmlFor="org-slug">
            Identifier <span className={form.optional}>optional</span>
          </label>
          <input
            id="org-slug"
            maxLength={ID_MAX}
            className={form.input}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="Derived from the name"
            aria-invalid={slugTooShort}
            aria-describedby="org-slug-help"
            disabled={create.isPending}
          />
          <p className={form.hint} id="org-slug-help">
            {/* Shown because it becomes the document id and cannot be changed
                afterwards without moving every subcollection under it. */}
            Will be <strong>{effectiveSlug || '—'}</strong>. This becomes the
            organization&rsquo;s permanent id and cannot be changed later.
          </p>
          {slugTooShort && (
            <p className={form.error}>
              That produces an identifier shorter than three characters. Type
              one directly.
            </p>
          )}
        </div>

        <div className={form.field} style={{ marginBottom: 'var(--sp-5)' }}>
          <label className={form.label} htmlFor="org-domain">
            Email domain <span className={form.optional}>optional</span>
          </label>
          <input
            id="org-domain"
            maxLength={URL_MAX}
            className={form.input}
            value={primaryDomain}
            onChange={(e) => setPrimaryDomain(e.target.value)}
            placeholder="hospital.org"
            disabled={create.isPending}
          />
        </div>

        <div className={form.field} style={{ marginBottom: 'var(--sp-5)' }}>
          <label className={form.label} htmlFor="org-admin">
            System Admin&rsquo;s email
          </label>
          <input
            id="org-admin"
            type="email"
            maxLength={EMAIL_MAX}
            className={form.input}
            value={systemAdminEmail}
            onChange={(e) => setSystemAdminEmail(e.target.value)}
            placeholder="it.director@hospital.org"
            aria-invalid={emailInvalid}
            aria-describedby={emailInvalid ? 'org-admin-error' : 'org-admin-help'}
            disabled={create.isPending}
            required
          />
          {emailInvalid ? (
            <p className={form.error} id="org-admin-error">
              Enter the System Admin&rsquo;s email address.
            </p>
          ) : (
            <p className={form.hint} id="org-admin-help">
              The first person at the hospital. They add everyone else, decide what each person may do,
              and set up single sign-on, and must use Google Authenticator. They are emailed now.
            </p>
          )}
        </div>

        {/*
          * Reminder email is no longer decided here. An organization on its
          * first day is importing a library and working out who each policy
          * should be asked of, and mail sent during that reaches real
          * addresses about work nobody meant to assign yet. Their System Admin
          * turns it on when they are ready, and you can still turn it on for
          * them from the organization's page afterwards.
          */}
        <p className={form.hint} style={{ marginBottom: 'var(--sp-5)' }}>
          Reminder email starts off. Their System Admin turns it on from Organization settings,
          Notifications, once the library is loaded and the audiences are right.
        </p>

        <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--sp-5)' }}>
          <legend className={form.label}>Add-ons they bought</legend>
          {(Object.keys(ADDON_LABEL) as AddonKey[]).map((key) => (
            <label key={key} className={field.roleToggle} style={{ display: 'block', marginBottom: 'var(--sp-2)' }}>
              <input
                type="checkbox"
                checked={addons.includes(key)}
                onChange={(e) => setAddons(e.target.checked ? [...addons, key] : addons.filter((k) => k !== key))}
                disabled={create.isPending}
              />
              <span>{ADDON_LABEL[key]}</span>
            </label>
          ))}
        </fieldset>

        {create.isSuccess && create.data.emailSent === false && (
          <div className={`${page.notice} ${page.noticeWarn}`} role="status">
            {create.data.message ?? 'The organization was created, but the invitation email could not be sent.'}{' '}
            <Link to={`/console/orgs/${create.data.orgId}`}>Open the organization</Link> to resend it.
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
          <button
            type="submit"
            className={own.googleBtn}
            style={{ width: 'auto', padding: '10px 18px' }}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create organization'}
          </button>
          <Link to="/console/orgs" className={own.plainBtn} style={{ textDecoration: 'none' }}>
            Cancel
          </Link>
        </div>
      </form>
    </>
  );
}
